import localforage from "localforage";
import { getAuthData } from "./LocalStorage";
import { ImBot, BotUtils, AuthContext, AgentGuardService } from "../../utils/libs";
import { BotApiServices } from "../api/BotApiServices.js";
import cloneDeep from "lodash/cloneDeep";
import isEmpty from "lodash/isEmpty";

const userId = getAuthData()?.user?.userId || null;

export const LFStorage = {};
export const LFStorageKeys = {
    DOMAINS: "DOMAINS",
    DEPENDENCIES: "DEPENDENCIES",
    OFFLINE_QUEUE: "OFFLINE_QUEUE",
    MESSAGES: "MESSAGES",
    FMTABLE_EDIT_BUFFER: "FMTABLE_EDIT_BUFFER",
    CONTACTS: 'CONTACTS',
    HEADER_TITLE: 'HEADER_TITLE',
    ACTIVE_ITEM: 'ACTIVE_ITEM',
    SELECTED_BOT: 'SELECTED_BOT',
    SELECTED_DOMAIN: 'SELECTED_DOMAIN',
    PREVIOUS_DOC_ID: 'PREVIOUS_DOC_ID'
};


export const initiateLFStorage = async (user) => {
    if (!user) return;

    const { userId, userName } = user;
    localforage.setDriver([localforage.INDEXEDDB, localforage.WEBSQL, localforage.LOCALSTORAGE]);
    const userStorage = localforage.createInstance({
        name: userId,
    });
    userStorage.config({
        storeName: userId,
        description: `DB for user ${userName}`,
    });
    try {
        await userStorage.ready();
        console.log("%c LFSTORAGE IS READY:", "color: green;", userStorage.driver());
        LFStorage[userId] = userStorage; // Store the instance in LFStorage after it’s ready
    } catch (error) {
        console.error("%c LFSTORAGE IS NOT READY:", "color: red;", error);
    }
};


export const saveDataInLFStorage = (key, value) => {
    const userId = getAuthData()?.user?.userId || null;
    if (!LFStorage[userId]) return;

    LFStorage[userId]
        .setItem(key, value)
        .then(() => {
            console.log(`%c Saved ${key} in LFStorage DB`, "color: green;", value);
        })
        .catch((err) => {
            console.error("%c LFStorage Error: ====", "color: red;", err);
        });
};


export const getDataFromLFStorage = async (userId, key) => {
    const userStorage = LFStorage[userId];
    if (!userStorage || typeof userStorage.getItem !== "function") return null;

    try {
        const value = await userStorage.getItem(key); // if getItem is async
        // const value = userStorage.getItem(key); // if getItem is sync
        return value;
    } catch (err) {
        console.error("%c LFStorage Error: ====", "color: red;", err);
        return null;
    }
};



export const removeDataFromLFStorage = (key) => {
    if (!LFStorage[userId]) return;

    LFStorage[userId]
        .removeItem(key)
        .then(() => true)
        .catch((err) => {
            console.error("%c LFStorage Error: ====", "color: red;", err);
        });
};


export const setBotsDependenciesList = async (bots) => {
    try {
        let dependenciesList = (await getDataFromLFStorage(LFStorageKeys.DEPENDENCIES)) || [];
        bots.forEach((bot) => {
            Object.entries(bot.dependencies).forEach(([key, value]) => {
                if (!isEmpty(value)) {
                    const checkDuplicateDependencies = dependenciesList.filter(
                        (dependency) =>
                            dependency.remoteDependencies === key && dependency.version === value.version
                    );
                    if (!checkDuplicateDependencies.length) {
                        dependenciesList.push({ ...value, remoteDependencies: key });
                    }
                }
            });
        });
        await Promise.all(
            dependenciesList.map(async (dependency) => {
                if (!dependency.Content?.length) {
                    if (dependency?.remoteDependencies === "agentGuardService") {
                        dependency.Content = AgentGuardService;
                    } else if (dependency?.remoteDependencies === "authContext") {
                        dependency.Content = AuthContext;
                    } else if (dependency?.remoteDependencies === "botUtils") {
                        dependency.Content = BotUtils;
                    } else if (dependency.url) {
                        try {
                            const botContent = await BotApiServices.downloadBotFile(dependency.url);
                            dependency.Content = botContent?.data;
                        } catch (error) {
                            console.error(`Error downloading dependency: ${dependency.url}`, error);
                        }
                    }
                }
                return dependency;
            })
        );

        saveDataInLFStorage(LFStorageKeys.DEPENDENCIES, dependenciesList);
        return dependenciesList;
    } catch (error) {
        console.error("%c Error in setBotsDependenciesList: ===", "color: red;", error);
    }
};


export const updateBotForSelectedDomainInLFStorage = async (domainName, bot) => {
    try {
        let botsList = await getDataFromLFStorage(userId, domainName);
        botsList = botsList?.filter((item) => item.botId !== bot.botId);
        // const response = await BotApiServices.downloadBotFile(bot.botUrl);
        // if (!response || response.status !== 200) {
        //     throw new Error("Failed to download bot file");
        // }
        // bot.botContent = response.data;
        if (botsList) {
            saveDataInLFStorage(domainName, [bot, ...botsList]);
        }
        else {
            saveDataInLFStorage(domainName, [bot]);
        }
        return botsList;
    } catch (error) {
        console.error("%c Error in updateBotForSelectedDomainInLFStorage: ===", "color: red;", error);
    }
};


export const setAllBotsForSelectedDomainInLFStorage = async (domainName, bots = []) => {
    try {
        let botsList = cloneDeep(bots);
        let selectedDomain = domainName || JSON.parse(localStorage.getItem("StoredDomain"))?.userDomain;
        // const loadBotContent = async (bot) => {
        //     if (bot.botId === "im-bot") {
        //         bot.botContent = ImBot;
        //     } else if (bot?.botContent) {
        //         return bot;
        //     } else {
        //         const response = await BotApiServices.downloadBotFile(bot.botUrl);
        //         if (!response || response.status !== 200) {
        //             throw new Error("Failed to download bot file");
        //         }
        //         bot.botContent = response.data;
        //         return bot;
        //     }
        // };
        // await Promise.all(botsList.filter((i) => !i.systemBot).map(loadBotContent));
        saveDataInLFStorage(selectedDomain, botsList);
        return botsList;
    } catch (error) {
        console.error("%c Error in setAllBotsForSelectedDomainInLFStorage: ===", "color: red;", error);
    }
};


export const downloadBotForSelectedDomainInLFStorage = async (domainName, bot) => {
    try {
        const response = await BotApiServices.downloadBotFile(bot.botUrl);
        if (!response || response.status !== 200) {
            throw new Error("Failed to download bot file");
        }
        const botContent = response.data;
        const botsList = (await getDataFromLFStorage(domainName)) || [];
        const updatedBotsList = botsList.filter((item) => item.botId !== bot.botId);
        const updatedData = [{ ...bot, botContent }, ...updatedBotsList];
        await saveDataInLFStorage(domainName, updatedData);
        return updatedData;
    } catch (error) {
        console.error("%c Error in downloadBotForSelectedDomainInLFStorage: ===>", "color: red;", error);
    }
};


export const storeSelectedConversationInLF = async (value) => {
    try {
        await localforage.setItem('SelectedConversation', value);
    } catch (error) {
        console.error('Error storing selected conversation:', error);
    }
};


export const getSelectedConversationFromLF = async () => {
    try {
        const selectedConversation = await localforage.getItem('SelectedConversation');
        return selectedConversation;
    } catch (error) {
        console.error('Error retrieving selected conversation:', error);
        return null;
    }
};


export const clearLFUserStoragePromise = () => {
    const auth = getAuthData();
    const userId = auth?.user?.userId;

    if (!userId) {
        return Promise.reject(new Error("User ID not found."));
    }

    // Ensure user storage is initialized
    if (!LFStorage[userId]) {
        LFStorage[userId] = localforage.createInstance({
            name: "MyAppStorage",
            storeName: `user-${userId}`,
        });
    }

    return LFStorage[userId].clear();
};


export const clearLFStorage = () => {
    const auth = getAuthData();
    const emailAddress = auth?.user?.emailAddress;
    if (!emailAddress) return;
    clearLFUserStoragePromise()
        .then(() => {
            console.log(`%cCleared LFStorage DB for user: ${emailAddress}`, "color: green;");
        })
        .catch((error) => {
            console.error("%cLFStorage Error in clearLFStorage:", "color: red;", error);
        });
};

export const loadAllDomainsListInLFStorage = async (domains) => {
    try {
        await saveDataInLFStorage(LFStorageKeys.DOMAINS, domains);
    } catch (error) {
        console.error(
            "%c Error while loading domains and domains details in LFStorage: ====",
            "color: red;",
            error
        );
    }
};
export const storeDomainSelected = (data) => {
    const myData = { ...data };
    localStorage.setItem('StoredDomain', JSON.stringify(myData));
};